import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  priorityLabels,
  statusLabels,
  categoryLabels,
  agentList,
  type SupportTicket,
  type TicketPriority,
  type TicketStatus,
} from '@/mocks/superAdminSupport';

interface TicketDetailModalProps {
  ticket: SupportTicket | null;
  onClose: () => void;
  onReply: (ticketId: number, message: string) => void;
  onChangePriority: (ticketId: number, newPriority: TicketPriority) => void;
  onChangeStatus: (ticketId: number, newStatus: TicketStatus) => void;
  onAssign: (ticketId: number, agentId: string) => void;
  onCloseTicket: (ticketId: number) => void;
  onTransferToLive: (ticket: SupportTicket) => void;
}

const priorityColorMap: Record<string, string> = {
  low: 'bg-secondary-100 text-secondary-700',
  normal: 'bg-sky-50 text-sky-600',
  high: 'bg-amber-50 text-amber-700',
  urgent: 'bg-red-50 text-red-500',
};

const statusColorMap: Record<string, string> = {
  open: 'bg-secondary-100 text-secondary-700',
  in_progress: 'bg-sky-50 text-sky-600',
  waiting_customer: 'bg-amber-50 text-amber-700',
  resolved: 'bg-emerald-50 text-emerald-600',
  closed: 'bg-background-200 text-foreground-500',
};

const statusIcons: Record<string, string> = {
  open: 'ri-checkbox-blank-circle-line',
  in_progress: 'ri-loader-4-line',
  waiting_customer: 'ri-user-voice-line',
  resolved: 'ri-check-double-line',
  closed: 'ri-close-circle-line',
};

function formatDateFull(d: string): string {
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} a las ${hours}:${mins}`;
}

function formatTime(d: string): string {
  const date = new Date(d);
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${mins}`;
}

export default function TicketDetailModal({
  ticket,
  onClose,
  onReply,
  onChangePriority,
  onChangeStatus,
  onAssign,
  onCloseTicket,
  onTransferToLive,
}: TicketDetailModalProps) {
  const [replyText, setReplyText] = useState('');
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setReplyText('');
    setShowPriorityMenu(false);
    setShowStatusMenu(false);
    setShowAgentMenu(false);
  }, [ticket?.id]);

  useEffect(() => {
    if (!ticket) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [ticket, onClose]);

  if (!ticket) return null;

  const isClosedOrResolved = ticket.status === 'closed' || ticket.status === 'resolved';

  const handleReply = () => {
    if (!replyText.trim()) return;
    onReply(ticket.id, replyText.trim());
    setReplyText('');
  };

  const handlePrioritySelect = (p: TicketPriority) => {
    setShowPriorityMenu(false);
    if (p === ticket.priority) return;
    Swal.fire({
      icon: 'question',
      title: '¿Cambiar prioridad?',
      text: `Vas a cambiar de ${priorityLabels[ticket.priority]} a ${priorityLabels[p]}.`,
      showCancelButton: true,
      confirmButtonText: 'Cambiar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) onChangePriority(ticket.id, p);
    });
  };

  const handleStatusSelect = (s: TicketStatus) => {
    setShowStatusMenu(false);
    if (s === ticket.status) return;
    Swal.fire({
      icon: 'question',
      title: '¿Cambiar estado?',
      text: `Vas a cambiar de "${statusLabels[ticket.status]}" a "${statusLabels[s]}".`,
      showCancelButton: true,
      confirmButtonText: 'Cambiar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) onChangeStatus(ticket.id, s);
    });
  };

  const handleAgentSelect = (agentId: string) => {
    setShowAgentMenu(false);
    if (agentId === ticket.assignedTo) return;
    const agent = agentList.find((a) => a.id === agentId);
    Swal.fire({
      icon: 'question',
      title: '¿Asignar agente?',
      text: `Vas a asignar este ticket a ${agent?.name ?? agentId}.`,
      showCancelButton: true,
      confirmButtonText: 'Asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) onAssign(ticket.id, agentId);
    });
  };

  const handleCloseTicketClick = () => {
    Swal.fire({
      icon: 'warning',
      title: '¿Cerrar ticket?',
      text: 'El ticket se marcará como cerrado y no se podrá reabrir.',
      showCancelButton: true,
      confirmButtonText: 'Cerrar ticket',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) onCloseTicket(ticket.id);
    });
  };

  const handleTransferToLiveClick = () => {
    Swal.fire({
      icon: 'question',
      title: '¿Pasar a Live Chat?',
      text: `Se creará una conversación en Live Support con ${ticket.radioName} incluyendo el historial de mensajes de este ticket. Podés hacerlo las veces que necesites.`,
      showCancelButton: true,
      confirmButtonText: 'Pasar a Live Chat',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#e5e5e5',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        onTransferToLive(ticket);
      }
    });
  };

  const assignedAgent = agentList.find((a) => a.id === ticket.assignedTo);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-3xl bg-background-50 border border-background-200 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="shrink-0 flex items-start gap-4 p-5 border-b border-background-200">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-foreground-500 text-[10px] font-mono font-semibold">{ticket.ticketNumber}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${priorityColorMap[ticket.priority]}`}>
                {priorityLabels[ticket.priority]}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColorMap[ticket.status]}`}>
                <i className={`${statusIcons[ticket.status]} text-[9px]`}></i>
                {statusLabels[ticket.status]}
              </span>
            </div>
            <h3 className="text-foreground-950 font-bold text-sm">{ticket.subject}</h3>
            <p className="text-foreground-400 text-[11px] mt-1">
              {ticket.radioName} ({ticket.frequency}) · {categoryLabels[ticket.category]} · Creado {formatDateFull(ticket.createdAt)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 rounded-xl bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Action buttons bar */}
        <div className="shrink-0 flex items-center gap-2 px-5 py-3 border-b border-background-100 overflow-x-auto">
          {/* Change status */}
          <div className="relative">
            <button
              onClick={() => { setShowStatusMenu(!showStatusMenu); setShowPriorityMenu(false); setShowAgentMenu(false); }}
              disabled={isClosedOrResolved}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-600 text-[10px] font-medium hover:border-foreground-300 hover:text-foreground-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-flag-line text-[10px]"></i>
              Estado
              <i className="ri-arrow-down-s-line text-[9px]"></i>
            </button>
            {showStatusMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowStatusMenu(false)}></div>
                <div className="absolute top-full left-0 mt-1 z-20 w-44 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                  {(['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'] as TicketStatus[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => handleStatusSelect(s)}
                      className={`w-full text-left px-3 py-2 text-[11px] font-medium flex items-center gap-2 hover:bg-background-100 transition-colors cursor-pointer
                        ${s === ticket.status ? 'text-foreground-950 bg-background-100' : 'text-foreground-600'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${s === 'open' ? 'bg-secondary-500' : s === 'in_progress' ? 'bg-sky-500' : s === 'waiting_customer' ? 'bg-amber-500' : s === 'resolved' ? 'bg-emerald-500' : 'bg-foreground-400'}`}></span>
                      {statusLabels[s]}
                      {s === ticket.status && <i className="ri-check-line text-[10px] ml-auto"></i>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Change priority */}
          <div className="relative">
            <button
              onClick={() => { setShowPriorityMenu(!showPriorityMenu); setShowStatusMenu(false); setShowAgentMenu(false); }}
              disabled={isClosedOrResolved}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-600 text-[10px] font-medium hover:border-foreground-300 hover:text-foreground-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-signal-wifi-line text-[10px]"></i>
              Prioridad
              <i className="ri-arrow-down-s-line text-[9px]"></i>
            </button>
            {showPriorityMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowPriorityMenu(false)}></div>
                <div className="absolute top-full left-0 mt-1 z-20 w-36 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                  {(['low', 'normal', 'high', 'urgent'] as TicketPriority[]).map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePrioritySelect(p)}
                      className={`w-full text-left px-3 py-2 text-[11px] font-medium flex items-center gap-2 hover:bg-background-100 transition-colors cursor-pointer
                        ${p === ticket.priority ? 'text-foreground-950 bg-background-100' : 'text-foreground-600'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${p === 'urgent' ? 'bg-red-500' : p === 'high' ? 'bg-amber-500' : p === 'normal' ? 'bg-sky-500' : 'bg-secondary-500'}`}></span>
                      {priorityLabels[p]}
                      {p === ticket.priority && <i className="ri-check-line text-[10px] ml-auto"></i>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Assign agent */}
          <div className="relative">
            <button
              onClick={() => { setShowAgentMenu(!showAgentMenu); setShowStatusMenu(false); setShowPriorityMenu(false); }}
              disabled={isClosedOrResolved}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-200 bg-background-50 text-foreground-600 text-[10px] font-medium hover:border-foreground-300 hover:text-foreground-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-user-settings-line text-[10px]"></i>
              {assignedAgent ? assignedAgent.name.split(' ')[0] : 'Asignar'}
              <i className="ri-arrow-down-s-line text-[9px]"></i>
            </button>
            {showAgentMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowAgentMenu(false)}></div>
                <div className="absolute top-full left-0 mt-1 z-20 w-48 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                  {agentList.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => handleAgentSelect(a.id)}
                      className={`w-full text-left px-3 py-2 hover:bg-background-100 transition-colors cursor-pointer
                        ${a.id === ticket.assignedTo ? 'bg-background-100' : ''}`}
                    >
                      <p className="text-[11px] font-semibold text-foreground-800">{a.name}</p>
                      <p className="text-[9px] text-foreground-400">{a.role}</p>
                      {a.id === ticket.assignedTo && <i className="ri-check-line text-[10px] ml-auto absolute right-3 top-2.5 text-foreground-600"></i>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Transfer to Live Chat */}
          <button
            onClick={handleTransferToLiveClick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 text-[10px] font-medium hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-forward-line text-[10px]"></i>
            Live Chat
          </button>

          {/* Close ticket */}
          {!isClosedOrResolved && (
            <button
              onClick={handleCloseTicketClick}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-500 text-[10px] font-medium hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap ml-auto"
            >
              <i className="ri-close-circle-line text-[10px]"></i>
              Cerrar ticket
            </button>
          )}
        </div>

        {/* ══════ NAVEGACIÓN RÁPIDA ══════ */}
        <div className="shrink-0 px-5 py-2.5 border-b border-background-100 flex items-center gap-1.5 overflow-x-auto">
          <span className="text-foreground-400 text-[9px] font-medium mr-1 shrink-0">Ir a:</span>
          {[
            { label: 'Radios', icon: 'ri-radio-line', path: '/sa/radios' },
            { label: 'Contratos', icon: 'ri-file-text-line', path: '/sa/contracts' },
            { label: 'Invoices', icon: 'ri-file-list-3-line', path: '/sa/invoices' },
            { label: 'Pagos', icon: 'ri-money-dollar-circle-line', path: '/sa/payments' },
            { label: 'Billing', icon: 'ri-bank-card-line', path: '/sa/billing' },
            { label: 'Frecuencias', icon: 'ri-radar-line', path: '/sa/frequencies' },
            { label: 'Planes', icon: 'ri-stack-line', path: '/sa/plans' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => { onClose(); navigate(item.path); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-background-200 bg-background-50 text-foreground-600 text-[10px] font-medium hover:border-foreground-300 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={`${item.icon} text-[9px]`}></i>
              {item.label}
            </button>
          ))}
        </div>

        {/* Description */}
        <div className="shrink-0 px-5 py-4 border-b border-background-100 bg-background-100/40">
          <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Descripción</p>
          <p className="text-foreground-700 text-xs leading-relaxed">{ticket.description}</p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">
            Conversación ({ticket.messages.length} mensajes)
          </p>
          {ticket.messages.map((msg) => {
            const isSupport = msg.from === 'support';
            return (
              <div key={msg.id} className={`flex gap-3 ${isSupport ? 'flex-row' : 'flex-row-reverse'}`}>
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold
                  ${isSupport ? 'bg-sky-100 text-sky-600' : 'bg-secondary-100 text-secondary-700'}`}>
                  {msg.author.charAt(0).toUpperCase()}
                </div>
                <div className={`flex-1 max-w-[70%] ${isSupport ? '' : 'flex flex-col items-end'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-foreground-800 text-[11px] font-semibold">{msg.author}</span>
                    <span className="text-foreground-400 text-[9px]">{formatDateFull(msg.date)}</span>
                  </div>
                  <div className={`rounded-2xl px-4 py-2.5 text-xs leading-relaxed
                    ${isSupport
                      ? 'bg-sky-50 text-foreground-800 rounded-tl-md border border-sky-100'
                      : 'bg-background-100 text-foreground-800 rounded-tr-md border border-background-200'
                    }`}>
                    {msg.content}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply form */}
        {!isClosedOrResolved && (
          <div className="shrink-0 p-5 border-t border-background-200">
            <div className="flex gap-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Escribí tu respuesta..."
                maxLength={500}
                className="flex-1 resize-none bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors min-h-[60px]"
              />
              <div className="flex flex-col justify-end gap-1.5 shrink-0">
                <span className="text-foreground-300 text-[9px] text-right">{replyText.length}/500</span>
                <button
                  onClick={handleReply}
                  disabled={!replyText.trim()}
                  className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-send-plane-fill text-xs mr-1"></i>
                  Responder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Closed/Resolved footer */}
        {isClosedOrResolved && (
          <div className="shrink-0 p-5 border-t border-background-200 bg-background-100/50 text-center">
            <p className="text-foreground-400 text-[10px]">
              Este ticket está {statusLabels[ticket.status].toLowerCase()}. No se pueden hacer más cambios.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}